import { Experiment, ExperimentClient, ExperimentUser, Variants } from "@amplitude/experiment-js-client";
import {
  loadAmplitudeSessionFirstPage,
  loadExperiments,
  loadFirstVisitTimestamp,
  loadUserData
} from "@/common/utils/storage";
import { bus } from "@/common/buses/metrics/bus";
import { isTrackingEnabled } from "@/common/utils/tracking";
import sessionService from "@/common/services/session/sessionService";
import { getDeviceType, getWindowWidth } from "@/common/utils/device";
import { isCookieDeclined } from "@/common/utils/cookie";
import seedrandom from "seedrandom";
import type { Types } from "@amplitude/analytics-browser";
import assignmentService from "@/common/services/experiment/assignmentService";

interface IVariants {
  [key: string]: string | boolean | null;
}

const API_KEY = import.meta.env.REACT_APP_AMPLITUDE_KEY;

class ExperimentService {
  private variants: null | IVariants = null;
  private exposures: string[] = [];
  private fetchPromise: Promise<[IVariants, Variants]> | null = null;
  private getClientPromise: Promise<ExperimentClient> | null = null;

  async fetchVariants(force: boolean = false) {
    if (force) {
      await this.clearVariants();
    }

    if (this.variants !== null) {
      return this.variants;
    }

    try {
      if (this.fetchPromise === null) {
        this.fetchPromise = Promise.all([
          assignmentService.getAll(),
          this.fetchRemoteVariants()
        ]);
      }

      const [assignments, remoteVariants] = await this.fetchPromise;

      this.fillVariants(assignments, remoteVariants);
    } catch (e) {
      console.error(e);
    } finally {
      this.fetchPromise = null;
    }
  }

  async getVariant(experimentKey: string, fallback: string | boolean, coverage: number = 1) {
    if (await isCookieDeclined(["analytics"])) {
      return fallback;
    }

    const localVariants = (loadExperiments() ?? {}) as IVariants;

    if (localVariants[experimentKey] !== undefined) {
      return localVariants[experimentKey] ?? fallback;
    }

    const experimentEnabled = await isTrackingEnabled(coverage);
    if (!experimentEnabled) {
      return fallback;
    }

    const variants = await this.getAllVariants();
    const variant = variants[experimentKey] ?? null;

    if (variant !== null) {
      if (!this.exposures.includes(experimentKey)) {
        bus.emit("experimentVariantExposure", {
          experimentKey,
          experimentVariant: variant
        });

        const client = await this.getClient();
        client.exposure(experimentKey);

        this.exposures.push(experimentKey);
      }

      return variant;
    }

    return fallback;
  }

  async getAllVariants(): Promise<IVariants> {
    if (this.variants === null) {
      await this.fetchVariants();
    }

    const localVariants = (loadExperiments() ?? {}) as IVariants;
    const fetchedVariants = this.variants ?? {};

    return {
      ...fetchedVariants,
      ...localVariants
    };
  }

  async clearVariants() {
    assignmentService.clear();
    this.variants = null;
    this.exposures = [];

    const client = await this.getClient();
    client.clear();
  }

  async getClient(): Promise<ExperimentClient> {
    if (!this.getClientPromise) {
      this.getClientPromise = new Promise((resolve) => {
        setTimeout(() => {
          resolve(
            Experiment.initializeWithAmplitudeAnalytics(API_KEY, {
              debug: import.meta.env.REACT_APP_AMPLITUDE_DEBUG === "1",
              fetchOnStart: false,
              pollOnStart: false,
              fetchTimeoutMillis: 200,
              retryFetchOnFailure: false
            })
          );
        }, 0);
      });
    }

    return await this.getClientPromise;
  }

  private fillVariants(assignments: IVariants, remoteVariants: Variants) {
    this.variants = {};

    for (const key in assignments) {
      this.variants[key] = assignments[key];
    }

    for (const key in remoteVariants) {
      this.variants[key] = remoteVariants[key].value ?? null;
    }
  }

  private async fetchRemoteVariants() {
    const client = await this.getClient();
    await client.start().catch((reason) => {
      console.debug(reason);
    });

    const experimentUser = await this.getExperimentUser();

    return client.fetch(experimentUser).then(client => client.all());
  }

  private async getExperimentUser() {
    const deviceId = (await sessionService.getSession()).session;
    const getDeviceScore = seedrandom(deviceId);

    const properties: { [key: string]: Types.ValidPropertyType } = {
      "Device Type": getDeviceType(getWindowWidth()),
      "Device Score": getDeviceScore(),
    };

    const firstPage = loadAmplitudeSessionFirstPage();

    if (firstPage) {
      properties["First Visited Page"] = firstPage;
    }

    const firstVisitTimestamp = loadFirstVisitTimestamp();

    if (firstVisitTimestamp) {
      properties["First Visit Timestamp"] = firstVisitTimestamp;
    }

    return {
      user_id: loadUserData()?.username,
      device_id: deviceId,
      user_properties: properties,
    } as ExperimentUser;
  }
}

export default new ExperimentService();
